import { apiService } from '../services/api';
import API_CONFIG from '../config/api.config';

// Role interface
export interface Role {
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  isSystem: boolean;
  isDeleted?: boolean;
  userCount: number;
  createdAt: string;
  updatedAt: string;
}

interface RoleUsersResponse {
  count: number;
  users: Array<{ id: string; email: string }>;
}

export const roleApi = {
  // Get all roles
  async getRoles(isActive?: boolean, includeDeleted: boolean = false): Promise<Role[]> {
    try {
      const params: any = { includeDeleted: includeDeleted.toString() };
      if (isActive !== undefined) {
        params.isActive = isActive.toString();
      }
      
      const response = await apiService.get<{ data: Role[] }>(API_CONFIG.ENDPOINTS.ROLES, { params });
      
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching roles:', error);
      return [];
    }
  },

  // Get role by ID
  async getRole(id: string): Promise<Role | undefined> {
    try {
      const response = await apiService.get<{ data: Role }>(`${API_CONFIG.ENDPOINTS.ROLES}/${id}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching role ${id}:`, error);
      return undefined;
    }
  },

  // Create a new role
  async createRole(roleData: Omit<Role, 'id' | 'createdAt' | 'updatedAt' | 'isDeleted' | 'userCount'>): Promise<{ data?: Role; error?: string }> {
    try {
      console.log('Creating role with data:', roleData);
      const response = await apiService.post<{ data: Role }>(
        API_CONFIG.ENDPOINTS.ROLES, 
        roleData
      );
      console.log('Role created successfully:', response.data);
      return { data: response.data.data };
    } catch (error: any) {
      console.error('Error creating role:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          data: error.config?.data
        }
      });
      
      let errorMessage = 'Failed to create role. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { 
        error: errorMessage
      };
    }
  },

  // Update a role
  async updateRole(id: string, roleData: Partial<Role>): Promise<{ data?: Role; error?: string }> {
    try {
      const response = await apiService.put<{ data: Role }>(
        `${API_CONFIG.ENDPOINTS.ROLES}/${id}`, 
        roleData
      );
      return { data: response.data.data };
    } catch (error) {
      console.error(`Error updating role ${id}:`, error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update role. Please try again.';
      return { 
        error: errorMessage
      };
    }
  },

  // Get users assigned to a role
  async getRoleUsers(roleId: string): Promise<RoleUsersResponse> {
    try {
      const response = await apiService.get<{ data: RoleUsersResponse }>(
        `${API_CONFIG.ENDPOINTS.ROLES}/${roleId}/users`
      );
      return response.data.data;
    } catch (error: unknown) {
      console.error(`Error fetching users for role ${roleId}:`, error);
      return { count: 0, users: [] };
    }
  },

  // Soft delete a role
  async deleteRole(id: string): Promise<{ success: boolean; message?: string }> {
    try {
      // First check if there are any users assigned to this role
      const { count: userCount } = await this.getRoleUsers(id);
      
      if (userCount > 0) {
        return { 
          success: false, 
          message: `Cannot delete role as it is assigned to ${userCount} user(s).` 
        };
      }
      
      // Perform soft delete
      await apiService.patch(`${API_CONFIG.ENDPOINTS.ROLES}/${id}`, { 
        isDeleted: true,
        isActive: false 
      });
      
      return { success: true };
    } catch (error) {
      console.error(`Error soft deleting role ${id}:`, error);
      return { 
        success: false, 
        message: 'Failed to delete role. Please try again.' 
      };
    }
  },
  
  // Restore a soft-deleted role
  async restoreRole(id: string): Promise<{ data?: Role; error?: string }> {
    try {
      const response = await apiService.patch<{ data: Role }>(
        `${API_CONFIG.ENDPOINTS.ROLES}/${id}/restore`,
        { isDeleted: false }
      );
      return { data: response.data.data };
    } catch (error) {
      console.error(`Error restoring role ${id}:`, error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to restore role';
      return { error: errorMessage };
    }
  }
};
